import { CommonModule } from '@angular/common';
import { OverlayModule, type ConnectedPosition } from '@angular/cdk/overlay';
import {
  Component, DestroyRef, ElementRef, EventEmitter, Input, OnChanges, Output,
  ViewChild, effect, inject, signal,
} from '@angular/core';
import { UiButtonDirective } from '@template-ui/directives';
import { workflowMenuIcons } from './workflow-navigation-authoring';
import { WorkflowOwnerViewState } from './workflow-owner-view-state';

let pickerIdentity = 0;

/** A visual choice over the installed workflow-menu icons; it never owns draft state. */
@Component({
  selector: 'phms-workflow-menu-icon-picker',
  imports: [CommonModule, OverlayModule, UiButtonDirective],
  templateUrl: './workflow-menu-icon-picker.component.html',
  styleUrl: './workflow-menu-icon-picker.component.scss',
})
export class WorkflowMenuIconPickerComponent implements OnChanges {
  @Input() value = 'pi pi-sitemap';
  @Input() fallbackIcon = 'pi pi-list';
  @Input() label = 'Workflow menu icon';
  @Input() disabled = false;
  @Input() active = true;
  @Input() context = '';
  @Output() valueChange = new EventEmitter<string>();
  @ViewChild('trigger') private trigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('panel') private panel?: ElementRef<HTMLElement>;
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ownerViews = inject(WorkflowOwnerViewState, { optional: true }) ?? new WorkflowOwnerViewState();
  readonly menuId = `workflow-menu-icon-${++pickerIdentity}`;
  readonly icons = workflowMenuIcons;
  readonly open = signal(false);
  activeIndex = 0;
  private focusRequest = 0;
  readonly positions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 5 },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 5 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -5 },
  ];
  constructor() {
    effect(() => { if (this.ownerViews.focusSuspended() && this.open()) this.close(false); });
  }
  ngOnChanges(): void {
    if (this.open()) this.close(false);
  }
  get safeIcon(): string {
    return this.icons.some(icon => icon.code === this.value) ? this.value
      : this.icons.some(icon => icon.code === this.fallbackIcon) ? this.fallbackIcon : 'pi pi-list';
  }
  get selectedLabel(): string { return this.icons.find(icon => icon.code === this.value)?.label ?? 'Unavailable saved icon'; }
  toggle(): void {
    if (this.destroyRef.destroyed || this.disabled || !this.active || this.ownerViews.focusSuspended()) return;
    if (this.open()) { this.close(); return; }
    this.activeIndex = Math.max(0, this.icons.findIndex(icon => icon.code === this.value));
    this.open.set(true);
  }
  choose(value: string): void {
    if (this.destroyRef.destroyed || this.disabled || !this.active || !this.open() || this.ownerViews.focusSuspended() || !this.icons.some(icon => icon.code === value)) return;
    if (value !== this.value) this.valueChange.emit(value);
    this.close();
  }
  close(restoreFocus = true): void {
    this.focusRequest++;
    this.open.set(false);
    if (restoreFocus && !this.destroyRef.destroyed && !this.disabled && this.active &&
      !this.ownerViews.focusSuspended() && !this.element.nativeElement.closest('[hidden],[inert]'))
      this.trigger?.nativeElement.focus({ preventScroll: true });
  }
  detached(): void { this.focusRequest++; this.open.set(false); }
  focusCurrent(): void {
    const request = ++this.focusRequest;
    queueMicrotask(() => {
      if (request !== this.focusRequest || this.destroyRef.destroyed || !this.open() || this.disabled || !this.active ||
        this.ownerViews.focusSuspended() || this.element.nativeElement.closest('[hidden],[inert]')) return;
      this.panel?.nativeElement.querySelectorAll<HTMLButtonElement>('[role=menuitemradio]')[this.activeIndex]?.focus();
    });
  }
  triggerKeydown(event: KeyboardEvent): void {
    if (!['ArrowDown', 'ArrowUp'].includes(event.key) || this.disabled) return;
    event.preventDefault();
    if (!this.open()) this.toggle();
    this.activeIndex = event.key === 'ArrowUp' ? this.icons.length - 1 : 0;
    this.focusCurrent();
  }
  menuKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.close(); return; }
    if (event.key === 'Tab') { this.close(); return; }
    const offset = ({ ArrowRight: 1, ArrowLeft: -1, ArrowDown: 4, ArrowUp: -4 } as Record<string, number>)[event.key];
    if (event.key !== 'Home' && event.key !== 'End' && offset === undefined) return;
    event.preventDefault(); event.stopPropagation();
    this.activeIndex = event.key === 'Home' ? 0 : event.key === 'End' ? this.icons.length - 1
      : (this.activeIndex + offset + this.icons.length) % this.icons.length;
    this.focusCurrent();
  }
}
